import { ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlanLimitStateService } from '../../features/plans/services/plan-limit-state.service';
import { ExpertRole } from '../../models/expert-role.model';
import { ChatInputMode, ChatItem } from '../../models/chat-ui.model';
import { ExpertRolesService } from '../../services/expert-roles/expert-roles.service';
import { ChatFeedbackUiService } from '../../services/chat/presentation/chat-feedback-ui.service';
import { ChatIdService } from '../../services/chat/presentation/chat-id.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { CHAT_NON_EXPERT_ROLE } from './chat-runtime.state';

export interface ChatRoleComposerState {
  expertRoles: ExpertRole[];
  selectedExpertRole: string | null;
  expertRolesLoading: boolean;
  expertRolesError: boolean;
  lastAppliedExpertRole: string | null;
  lastRequestedExpertRole: string | null;
  isRoleDropdownOpen: boolean;
  showToolsPanel: boolean;
  armedGenerateSummary: boolean;
  selectedInputMode: ChatInputMode;
  hoverRating: number;
  feedbackRating: number;
  topK: number;
}

export class ChatRoleComposerController {
  constructor(
    private readonly state: ChatRoleComposerState,
    private readonly rolesService: ExpertRolesService,
    private readonly storage: ChatStorageService,
    private readonly feedback: ChatFeedbackUiService,
    private readonly ids: ChatIdService,
    private readonly planLimits: PlanLimitStateService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  get isSelectorVisible(): boolean { return this.state.expertRoles.length > 0; }
  get isLastRoleDegraded(): boolean {
    return !!this.state.lastRequestedExpertRole && !this.state.lastAppliedExpertRole;
  }
  get defaultRole(): string | null {
    return this.state.expertRoles.some((role) => role.code === CHAT_NON_EXPERT_ROLE)
      ? CHAT_NON_EXPERT_ROLE
      : null;
  }
  get selectedDisplayName(): string | null {
    if (!this.state.selectedExpertRole) return null;
    return this.state.expertRoles.find(
      (role) => role.code === this.state.selectedExpertRole
    )?.displayName || null;
  }
  get selectedIcon(): string {
    return this.roleIcon(this.state.expertRoles.find(
      (role) => role.code === this.state.selectedExpertRole
    ));
  }
  get isCurrentModeBlocked(): boolean {
    const code = this.state.selectedInputMode === 'analisis_csv'
      ? 'monthly_csv_queries'
      : 'monthly_chat_queries';
    return this.planLimits.isBlocking(code);
  }

  closeDropdown(): void {
    if (!this.state.isRoleDropdownOpen) return;
    this.state.isRoleDropdownOpen = false;
    this.changeDetector.markForCheck();
  }

  selectRole(code: string | null): void {
    this.state.selectedExpertRole = code;
    if (code) this.storage.setSelectedExpertRole(code);
    else this.storage.clearSelectedExpertRole();
    this.changeDetector.markForCheck();
  }

  roleIcon(role: ExpertRole | null | undefined): string {
    return role?.icon?.trim() ? role.icon : 'fas fa-user-tie';
  }

  hasBadge(message: ChatItem): boolean {
    return message.type === 'user'
      && !!message.expertRole
      && message.expertRole !== CHAT_NON_EXPERT_ROLE;
  }

  badge(slug: string): { icon: string; name: string } {
    const role = this.state.expertRoles.find((item) => item.code === slug);
    return role
      ? { icon: this.roleIcon(role), name: role.displayName }
      : { icon: 'fas fa-user-tie', name: this.capitalizeSlug(slug) };
  }

  capitalizeSlug(slug: string): string {
    const value = slug.trim();
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  load(destroyed$: Observable<void>): void {
    this.state.expertRolesLoading = true;
    this.state.expertRolesError = false;
    this.rolesService.getRoles().pipe(takeUntil(destroyed$)).subscribe({
      next: (roles) => {
        this.state.expertRoles = roles;
        this.state.expertRolesLoading = false;
        this.restore();
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.state.expertRoles = [];
        this.state.expertRolesLoading = false;
        this.state.expertRolesError = true;
        this.changeDetector.markForCheck();
      },
    });
  }

  restore(): void {
    const stored = this.storage.getSelectedExpertRole();
    if (stored && this.state.expertRoles.some((role) => role.code === stored)) {
      this.state.selectedExpertRole = stored;
      return;
    }
    this.state.selectedExpertRole = this.defaultRole;
    this.storage.clearSelectedExpertRole();
  }

  clear(): void {
    this.state.selectedExpertRole = this.defaultRole;
    this.state.lastAppliedExpertRole = null;
    this.state.lastRequestedExpertRole = null;
    this.state.isRoleDropdownOpen = false;
    this.storage.clearSelectedExpertRole();
  }

  autoGrow(target: EventTarget | null): void {
    const textarea = target as HTMLTextAreaElement | null;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }

  selectInputMode(mode: ChatInputMode): void {
    this.state.selectedInputMode = mode;
    this.state.showToolsPanel = false;
  }
  getStarColor(star: number): string {
    return this.feedback.getStarColor(star, this.state.hoverRating, this.state.feedbackRating);
  }
  getStarClass(star: number): string {
    return this.feedback.getStarClass(star, this.state.hoverRating, this.state.feedbackRating);
  }
  generateMessageEntityId(): string { return this.ids.createMessageEntityId(); }
}
